import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Loader } from '@mantine/core';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { useMediaQueryIndex, useModalState } from '../../../helpers/hooks';
import AppCard from '../../common/AppCard';
import PaginationList from '../../common/PaginationList';
import AppCheckbox from '../../common/AppCheckbox';
import ConfirmModal from '../../common/ConfirmModal';
import { triggerNotification } from '../../../helpers/notification';

const StoreAdminListView = ({ ecomStore, loading }) => {
  const { isLargeMobileOrSmaller, isTabletOrSmaller } = useMediaQueryIndex();
  const { state: authState } = useContext(AuthContext);
  const { revokeEcomStoreAdmins, restoreEcomStoreAdmins } = useContext(
    StoreContext
  );
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const [formState, setFormState] = useState({
    currentAdminsIndex: 1,
    revokedAdminsIndex: 1,
    selectedPkEcomStoreAdmin: []
  });

  const selectedAdminsToRevoke =
    ecomStore?.ecomStoreAdmins.filter(
      (a) =>
        formState.selectedPkEcomStoreAdmin.includes(a.pkEcomStoreAdmin) &&
        !a.deleted
    ) || [];
  const selectedAdminsToRestore =
    ecomStore?.ecomStoreAdmins.filter(
      (a) =>
        formState.selectedPkEcomStoreAdmin.includes(a.pkEcomStoreAdmin) &&
        a.deleted
    ) || [];

  const onSelectAdmin = (selected, pkEcomStoreAdmin) => {
    setFormState({
      ...formState,
      selectedPkEcomStoreAdmin: selected
        ? [
            ...formState.selectedPkEcomStoreAdmin.filter(
              (pk) => pk !== pkEcomStoreAdmin
            )
          ]
        : [...formState.selectedPkEcomStoreAdmin, pkEcomStoreAdmin]
    });
  };

  return (
    <AppStack style={{ flex: 1, alignItems: 'center' }}>
      <AppStack
        p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
        style={{
          flex: 1,
          margin: 'auto',
          width: '100%',
          maxWidth: 900,
          paddingTop: 16,
          gap: 16
        }}
      >
        <AppFlexbox
          p={{ base: '0px 8px', sm: 0 }}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <AppText style={{ fontSize: 22, fontWeight: 700 }}>
            Store admins
          </AppText>
        </AppFlexbox>
        <AppStack style={{ gap: isLargeMobileOrSmaller ? 20 : 50 }}>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="sm"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 8 }}>
              <AppFlexbox
                style={{
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 28
                }}
              >
                <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                  Admins granted access
                </AppText>

                {selectedAdminsToRevoke.length > 0 && (
                  <Button
                    color="red"
                    onClick={() => {
                      onOpenModal('revoke');
                    }}
                    size="compact-sm"
                  >
                    Revoke access
                  </Button>
                )}
              </AppFlexbox>

              {loading ? (
                <AppStack style={{ alignItems: 'center', padding: 20 }}>
                  <Loader color="dark" size={40} />
                </AppStack>
              ) : (
                <>
                  {ecomStore.ecomStoreAdmins.filter((a) => !a.deleted).length >
                    0 && (
                    <AppText
                      style={{
                        fontSize: 14,
                        color: '#666',
                        textAlign: 'center',
                        fontStyle: 'italic'
                      }}
                    >
                      The following have been granted access to manage this
                      store.
                    </AppText>
                  )}

                  <PaginationList
                    emptyMessage="No users have been granted access to this store."
                    items={ecomStore.ecomStoreAdmins
                      .filter((a) => !a.deleted)
                      .map((a) => {
                        const selected = formState.selectedPkEcomStoreAdmin.includes(
                          a.pkEcomStoreAdmin
                        );
                        const disabled =
                          a.user.pkUser === authState.userData.user.pkUser;

                        return (
                          <AppFlexbox
                            key={a.pkEcomStoreAdmin}
                            onClick={() => {
                              if (!disabled) {
                                onSelectAdmin(selected, a.pkEcomStoreAdmin);
                              }
                            }}
                            style={{
                              padding: 8,
                              alignItems: 'center',
                              gap: 8,
                              cursor: disabled ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <AppCheckbox
                              checked={selected}
                              disabled={disabled}
                              onChange={() => {}}
                            />
                            <AppStack style={{ gap: 0 }}>
                              <AppText
                                style={{ fontSize: 14, fontWeight: 500 }}
                              >
                                {a.user.name}
                              </AppText>
                              <AppText style={{ fontSize: 14, color: '#666' }}>
                                {a.user.email}
                              </AppText>
                            </AppStack>
                          </AppFlexbox>
                        );
                      })}
                    itemsPerPage={10}
                    onPageChange={(currentAdminsIndex) =>
                      setFormState({
                        ...formState,
                        currentAdminsIndex
                      })
                    }
                    pageIndex={formState.currentAdminsIndex}
                  />
                </>
              )}
            </AppStack>
          </AppCard>

          {ecomStore?.ecomStoreAdmins.filter((a) => a.deleted).length > 0 && (
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="sm"
              style={{ padding: 16 }}
              withBorder
            >
              <AppStack style={{ gap: 8 }}>
                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 28
                  }}
                >
                  <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                    Revoked admins
                  </AppText>

                  {selectedAdminsToRestore.length > 0 && (
                    <Button
                      color="blue"
                      onClick={() => {
                        onOpenModal('restore');
                      }}
                      size="compact-sm"
                    >
                      Restore access
                    </Button>
                  )}
                </AppFlexbox>

                <AppText
                  style={{
                    fontSize: 14,
                    color: '#666',
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}
                >
                  The following can no longer view or manage this store.
                </AppText>

                <PaginationList
                  items={ecomStore.ecomStoreAdmins
                    .filter((a) => a.deleted)
                    .map((a) => {
                      const selected = formState.selectedPkEcomStoreAdmin.includes(
                        a.pkEcomStoreAdmin
                      );
                      const disabled =
                        a.user.pkUser === authState.userData.user.pkUser;

                      return (
                        <AppFlexbox
                          key={a.pkEcomStoreAdmin}
                          onClick={() => {
                            if (!disabled) {
                              onSelectAdmin(selected, a.pkEcomStoreAdmin);
                            }
                          }}
                          style={{
                            padding: 8,
                            alignItems: 'center',
                            gap: 8,
                            cursor: disabled ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <AppCheckbox checked={selected} onChange={() => {}} />
                          <AppStack style={{ gap: 0 }}>
                            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                              {a.user.name}
                            </AppText>
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              {a.user.email}
                            </AppText>
                          </AppStack>
                        </AppFlexbox>
                      );
                    })}
                  itemsPerPage={10}
                  onPageChange={(revokedAdminsIndex) =>
                    setFormState({
                      ...formState,
                      revokedAdminsIndex
                    })
                  }
                  pageIndex={formState.revokedAdminsIndex}
                />
              </AppStack>
            </AppCard>
          )}
        </AppStack>

        <ConfirmModal
          cancelActionText="Cancel"
          confirmActionColor="red"
          confirmActionText="Yes, revoke access"
          isLoading={modalState.loading}
          isOpen={modalState.isOpen && modalState.action === 'revoke'}
          onCancel={onCloseModal}
          onConfirm={() => {
            onChangeModalLoading(true);
            revokeEcomStoreAdmins(
              ecomStore.pkEcomStore,
              selectedAdminsToRevoke.map((a) => a.pkEcomStoreAdmin),
              () => {
                triggerNotification('Access revoked', 'success');
                onCloseModal();
                setFormState({
                  ...formState,
                  selectedPkEcomStoreAdmin: [
                    ...formState.selectedPkEcomStoreAdmin.filter(
                      (pk) =>
                        !selectedAdminsToRevoke.some(
                          (a) => a.pkEcomStoreAdmin === pk
                        )
                    )
                  ]
                });
              },
              (e) => {
                onChangeModalLoading(false);
                triggerNotification(e);
              }
            );
          }}
          title="Revoke admin access"
        >
          <AppStack>
            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to revoke admin access for{' '}
              <span style={{ fontWeight: 500 }}>
                {selectedAdminsToRevoke.length === 1
                  ? selectedAdminsToRevoke[0].user.name
                  : `${selectedAdminsToRevoke.length} admins`}
              </span>
              ?
            </AppText>

            <AppText style={{ fontSize: 14 }}>
              They will no longer be able to view or manage this store.
            </AppText>
          </AppStack>
        </ConfirmModal>

        <ConfirmModal
          cancelActionText="Cancel"
          confirmActionColor="blue"
          confirmActionText="Yes, restore access"
          isLoading={modalState.loading}
          isOpen={modalState.isOpen && modalState.action === 'restore'}
          onCancel={onCloseModal}
          onConfirm={() => {
            onChangeModalLoading(true);
            restoreEcomStoreAdmins(
              ecomStore.pkEcomStore,
              selectedAdminsToRestore.map((a) => a.pkEcomStoreAdmin),
              () => {
                triggerNotification('Access restored', 'success');
                onCloseModal();
                setFormState({
                  ...formState,
                  selectedPkEcomStoreAdmin: [
                    ...formState.selectedPkEcomStoreAdmin.filter(
                      (pk) =>
                        !selectedAdminsToRestore.some(
                          (a) => a.pkEcomStoreAdmin === pk
                        )
                    )
                  ]
                });
              },
              (e) => {
                onChangeModalLoading(false);
                triggerNotification(e);
              }
            );
          }}
          title="Revoke admin access"
        >
          <AppStack>
            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to restore admin access for{' '}
              <span style={{ fontWeight: 500 }}>
                {selectedAdminsToRestore.length === 1
                  ? selectedAdminsToRestore[0].user.name
                  : `${selectedAdminsToRestore.length} admins`}
              </span>
              ?
            </AppText>

            <AppText style={{ fontSize: 14 }}>
              They will be able to view and manage this store again.
            </AppText>
          </AppStack>
        </ConfirmModal>
      </AppStack>
    </AppStack>
  );
};

StoreAdminListView.propTypes = {
  ecomStore: PropTypes.object,
  loading: PropTypes.bool
};

export default StoreAdminListView;
